import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, SlidersHorizontal, Droplets, Archive, X } from 'lucide-react';
import { useStore } from '@/hooks/useStore';
import PlantCard from '@/components/PlantCard';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useTelegramHaptic, useTelegramPopup } from '@/hooks/telegram';

type WaterFilter = 'all' | 'needs_water' | 'ok';
type SortMode = 'watering' | 'name' | 'date' | 'location';

export default function HomePage() {
  const navigate = useNavigate();
  const haptic = useTelegramHaptic();
  const { showConfirm } = useTelegramPopup();
  const { plants, isLoadingPlants, waterAllPlants } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [waterFilter, setWaterFilter] = useState<WaterFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('watering');
  const [isWateringAll, setIsWateringAll] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const plantsNeedingWater = plants.filter(
    (p) => p.wateringStatus?.status === 'soon' ||
           p.wateringStatus?.status === 'overdue' ||
           p.wateringStatus?.status === 'critical'
  );

  const showNeedsWaterSection = waterFilter === 'all' && !searchQuery && plantsNeedingWater.length > 0;
  const showFilterIcons = plants.length >= 15;
  const hasActiveFilters = waterFilter !== 'all' || sortMode !== 'watering' || searchQuery.trim() !== '';

  const filteredPlants = useMemo(() => {
    let result = [...plants];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.nickname.toLowerCase().includes(q) ||
          p.species?.commonNameRu?.toLowerCase().includes(q) ||
          p.species?.scientificName?.toLowerCase().includes(q) ||
          p.customSpecies?.toLowerCase().includes(q)
      );
    }

    if (waterFilter === 'needs_water') {
      result = result.filter(
        (p) =>
          p.wateringStatus?.status === 'soon' ||
          p.wateringStatus?.status === 'overdue' ||
          p.wateringStatus?.status === 'critical'
      );
    } else if (waterFilter === 'ok') {
      result = result.filter(
        (p) => p.wateringStatus?.status === 'ok'
      );
    }

    if (sortMode === 'name') {
      result.sort((a, b) => a.nickname.localeCompare(b.nickname, 'ru'));
    } else if (sortMode === 'date') {
      result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortMode === 'location') {
      result.sort((a, b) => {
        const locA = a.location || '';
        const locB = b.location || '';
        return locA.localeCompare(locB, 'ru');
      });
    }

    return result;
  }, [plants, searchQuery, waterFilter, sortMode]);

  const locationGroups = useMemo(() => {
    if (sortMode !== 'location') return null;
    const plantsToGroup = showNeedsWaterSection
      ? filteredPlants.filter((p) => !plantsNeedingWater.includes(p))
      : filteredPlants;
    const groups: Record<string, typeof filteredPlants> = {};
    for (const plant of plantsToGroup) {
      const loc = plant.location || 'Без расположения';
      if (!groups[loc]) groups[loc] = [];
      groups[loc].push(plant);
    }
    return groups;
  }, [filteredPlants, sortMode, showNeedsWaterSection, plantsNeedingWater]);

  const handleAddPlant = () => {
    haptic('light');
    navigate('/add');
  };

  const handleWaterAll = async () => {
    if (isWateringAll || plantsNeedingWater.length === 0) return;
    const confirmed = await showConfirm(
      `Полить ${plantsNeedingWater.length} ${plantsNeedingWater.length === 1 ? 'растение' : plantsNeedingWater.length < 5 ? 'растения' : 'растений'}?`
    );
    if (!confirmed) return;
    haptic('medium');
    setIsWateringAll(true);
    try {
      await waterAllPlants(plantsNeedingWater.map((p) => p.id));
      haptic('success');
    } catch {
      haptic('error');
    } finally {
      setIsWateringAll(false);
    }
  };

  const handleToggleFilter = () => {
    haptic('light');
    setIsFilterOpen(!isFilterOpen);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setWaterFilter('all');
    setSortMode('watering');
    setIsFilterOpen(false);
  };

  if (isLoadingPlants) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 py-3 safe-area-top">
      {/* Plants list */}
      {plants.length > 0 ? (
        <div>
          {/* Needs water section */}
          {showNeedsWaterSection && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-orange-600">
                  💧 Нужен полив
                </h2>
                <button
                  onClick={handleWaterAll}
                  disabled={isWateringAll}
                  className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 transition-all active:scale-95 ${
                    isWateringAll ? 'opacity-50' : ''
                  }`}
                  style={{
                    background: 'rgba(34, 197, 94, 0.15)',
                    color: 'rgb(34, 197, 94)',
                  }}
                >
                  <Droplets className={`w-3.5 h-3.5 ${isWateringAll ? 'animate-pulse' : ''}`} />
                  Полить все ({plantsNeedingWater.length})
                </button>
              </div>
              <div className="space-y-2">
                {plantsNeedingWater.map((plant) => (
                  <PlantCard key={plant.id} plant={plant} />
                ))}
              </div>
            </div>
          )}

          {/* All plants header with optional filter icons */}
          {waterFilter === 'all' && !searchQuery && (
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-semibold">
                Все растения
                <span className="text-sm font-normal text-tg-hint ml-2">{plants.length}</span>
              </h2>
              {showFilterIcons && (
                <div className="flex gap-1.5">
                  <button
                    onClick={handleToggleFilter}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      isFilterOpen || hasActiveFilters ? 'bg-plant-green-500/15 text-plant-green-500' : 'text-tg-hint'
                    }`}
                  >
                    <Search className="w-4.5 h-4.5" />
                  </button>
                  <button
                    onClick={handleToggleFilter}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      isFilterOpen || hasActiveFilters ? 'bg-plant-green-500/15 text-plant-green-500' : 'text-tg-hint'
                    }`}
                  >
                    <SlidersHorizontal className="w-4.5 h-4.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Expandable filter panel */}
          {isFilterOpen && showFilterIcons && (
            <div className="mb-3 tg-card p-3 space-y-2.5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tg-hint" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Поиск растения..."
                  className="tg-input !pl-9 !py-1.5 text-sm"
                  autoFocus
                />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {([
                  { id: 'all' as WaterFilter, label: 'Все' },
                  { id: 'needs_water' as WaterFilter, label: 'Полив' },
                  { id: 'ok' as WaterFilter, label: 'В порядке' },
                ] as const).map((chip) => (
                  <button
                    key={chip.id}
                    onClick={() => {
                      haptic('selection');
                      setWaterFilter(chip.id);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                      waterFilter === chip.id
                        ? 'bg-plant-green-500 text-white'
                        : 'glass-circle text-tg-hint'
                    }`}
                  >
                    {chip.label}
                    {chip.id === 'needs_water' && plantsNeedingWater.length > 0 && (
                      <span className="ml-1">({plantsNeedingWater.length})</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {([
                  { id: 'watering' as SortMode, label: 'По поливу' },
                  { id: 'name' as SortMode, label: 'По имени' },
                  { id: 'date' as SortMode, label: 'По дате' },
                  { id: 'location' as SortMode, label: 'По комнатам' },
                ] as const).map((chip) => (
                  <button
                    key={chip.id}
                    onClick={() => {
                      haptic('selection');
                      setSortMode(chip.id);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                      sortMode === chip.id
                        ? 'bg-plant-green-500/70 text-white'
                        : 'glass-circle text-tg-hint'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-1 text-xs text-tg-hint"
                >
                  <X className="w-3 h-3" />
                  Сбросить
                </button>
              )}
            </div>
          )}

          {/* Plant list */}
          {waterFilter === 'all' && !searchQuery && sortMode === 'location' && locationGroups ? (
            <div className="space-y-4">
              {Object.entries(locationGroups).map(([loc, groupPlants]) => (
                <div key={loc}>
                  <h3 className="text-sm font-medium text-tg-hint mb-2">
                    {loc === 'Без расположения' ? '📍' : '🏠'} {loc}
                    <span className="ml-1">({groupPlants.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {groupPlants.map((plant) => (
                      <PlantCard key={plant.id} plant={plant} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : waterFilter === 'all' && !searchQuery ? (
            <div className="space-y-2">
              {(showNeedsWaterSection
                ? filteredPlants.filter((p) => !plantsNeedingWater.includes(p))
                : filteredPlants
              ).map((plant) => (
                <PlantCard key={plant.id} plant={plant} />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPlants.length > 0 ? (
                filteredPlants.map((plant) => (
                  <PlantCard key={plant.id} plant={plant} />
                ))
              ) : (
                <p className="text-center text-tg-hint py-8">
                  Ничего не найдено
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center py-12">
          <div className="w-24 h-24 rounded-full glass-circle-green flex items-center justify-center mb-6 animate-float">
            <span className="text-5xl">🌱</span>
          </div>
          <h2 className="text-xl font-semibold mb-2">Пока пусто</h2>
          <p className="text-tg-hint text-center mb-6 max-w-xs">
            Добавьте своё первое растение и начните получать напоминания о поливе!
          </p>
          <button
            onClick={handleAddPlant}
            className="tg-button flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>Добавить растение</span>
          </button>
        </div>
      )}

      {/* Archive link */}
      {plants.length > 0 && (
        <button
          onClick={() => navigate('/archive')}
          className="w-full py-3 flex items-center justify-center gap-2 text-sm text-tg-hint"
        >
          <Archive className="w-4 h-4" />
          Архив растений
        </button>
      )}

      {/* Floating add button */}
      {plants.length > 0 && (
        <button
          onClick={handleAddPlant}
          className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full flex items-center justify-center active:scale-95 transition-transform"
          style={{
            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.4)',
            color: 'white',
          }}
        >
          <Plus className="w-7 h-7" />
        </button>
      )}
    </div>
  );
}
